import { useTranslation } from "react-i18next";
import { Form, Grid, GridColumn, Icon, Message } from "semantic-ui-react";

import { useCan } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES, PIN_TYPES } from "models";
import { NoteAttachments } from "modules/AppointmentDetails/components/Interventions/components";
import "modules/AppointmentDetails/components/Interventions/components/PinCategory/components/PinCategoryForm/PinCategoryForm.scss";
import { usePinCategoryForm } from "modules/AppointmentDetails/components/Interventions/components/PinCategory/components/PinCategoryForm/usePinCategoryForm";

import type { DropdownItemProps } from "components";
import type { Appointment, Intervention, PIN_ORDER_STATUSES, PIN_STATUSES, Pin, WARRANTY_TYPES } from "models";
import type { NoteAttachment } from "modules/AppointmentDetails/components/Interventions/components";
import type { Dispatch, SetStateAction } from "react";

type PinCategoryFormProps = {
  selectedPinTypeOption: DropdownItemProps<PIN_TYPES>;
  intervention: Intervention;
  hasChanges: boolean;
  appointment?: Appointment;
  pinStatusOptions: DropdownItemProps<PIN_STATUSES>[];
  pinOrderOptions: DropdownItemProps<PIN_ORDER_STATUSES>[];
  pinWarrantyOptions: DropdownItemProps<WARRANTY_TYPES>[];
  attachments: NoteAttachment[];
  openModal: () => void;
  setHasChanges: Dispatch<SetStateAction<boolean>>;
  setAttachments: Dispatch<SetStateAction<NoteAttachment[]>>;
};

export type PinCategoryFormValues = Pick<Pin, "support_nr" | "claim_nr" | "ref_nr" | "keep_parts" | "note" | "visible_important_items" | "order_status"> & {
  warranty_type_id: WARRANTY_TYPES | null;
  pin_status_id: PIN_STATUSES | null;
  note_suggestion: number | null;
};

const FormHelper = createFormHelper<PinCategoryFormValues>();

export const PinCategoryForm = ({
  intervention,
  selectedPinTypeOption,
  hasChanges,
  appointment,
  attachments,
  pinOrderOptions,
  pinStatusOptions,
  pinWarrantyOptions,
  openModal,
  setHasChanges,
  setAttachments
}: PinCategoryFormProps) => {
  const { t } = useTranslation();

  const canAddFullPin = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinFull);
  const canAddPinNote = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.PinNote);
  const { isButtonDisabled, noteSuggestionOptions, orderStatus, isNoteRequired, handleKeepPartsCheckboxChange, handleNoteSuggestionDropdownChange } = usePinCategoryForm({
    selectedPinTypeOption,
    hasChanges,
    appointment,
    attachments,
    setHasChanges,
    setAttachments
  });

  return (
    <div className="PinCategoryForm">
      <Grid columns={16}>
        <GridColumn width={5}>
          <Form.Field>
            <label>{t("v8_support", "Support")} #</label>
            <FormHelper.Field.Input name="support_nr" inputProps={{ fluid: true, disabled: !canAddFullPin }} />
          </Form.Field>
        </GridColumn>

        <GridColumn width={5}>
          <Form.Field>
            <label>{t("v8_claim", "Claim")} #</label>
            <FormHelper.Field.Input name="claim_nr" inputProps={{ fluid: true, disabled: !canAddFullPin }} />
          </Form.Field>
        </GridColumn>

        <GridColumn width={4}>
          <Form.Field>
            <label>{t("v8_ref", "Ref")} #</label>
            <FormHelper.Field.Input name="ref_nr" inputProps={{ fluid: true, disabled: !canAddFullPin }} />
          </Form.Field>
        </GridColumn>
        <GridColumn width={2} verticalAlign="bottom">
          <Form.Field>
            <div className="checkbox-container">
              <Icon className="setting" color="grey" />
              <span>{t("v8_keep_parts", "Keep Parts")}</span>
              <FormHelper.Field.Checkbox
                name="keep_parts"
                formFieldProps={{ className: "checkbox-form-field", disabled: !canAddFullPin }}
                checkboxProps={{ className: "checkbox-box" }}
                onChange={value => handleKeepPartsCheckboxChange(value)}
              />
            </div>
          </Form.Field>
        </GridColumn>
      </Grid>

      <Grid columns={3}>
        <GridColumn>
          <Form.Field>
            <label>{t("v8_order_status", "Order status")}</label>
            <FormHelper.Field.Dropdown
              name="order_status"
              options={pinOrderOptions}
              dropdownProps={{
                disabled: !canAddFullPin,
                fluid: true,
                placeholder: t("v8_select_order_status", "Select order status"),
                clearable: !!orderStatus
              }}
            />
          </Form.Field>
        </GridColumn>

        {selectedPinTypeOption.value === PIN_TYPES.Warranty && (
          <GridColumn>
            <Form.Field>
              <label>{t("v8_warranty_type", "Warranty type")}</label>
              <FormHelper.Field.Dropdown
                name="warranty_type_id"
                options={pinWarrantyOptions}
                dropdownProps={{
                  disabled: !canAddFullPin,
                  fluid: true,
                  placeholder: t("v8_select_type", "Select type")
                }}
              />
            </Form.Field>
          </GridColumn>
        )}
        {selectedPinTypeOption.value !== PIN_TYPES.Remarks && (
          <GridColumn>
            <Form.Field>
              <label>{t("v8_pin_status", "Pin Status")}</label>
              <FormHelper.Field.Dropdown
                name="pin_status_id"
                options={pinStatusOptions}
                dropdownProps={{
                  disabled: !canAddFullPin,
                  fluid: true,
                  placeholder: t("v8_select_status", "Select status")
                }}
              />
            </Form.Field>
          </GridColumn>
        )}
      </Grid>

      <Grid columns={16}>
        <GridColumn width={14}>
          {noteSuggestionOptions.length > 0 && (
            <Form.Field>
              <label>{t("v8_note", "Note")}</label>
              <FormHelper.Field.Dropdown
                name="note_suggestion"
                options={noteSuggestionOptions}
                dropdownProps={{
                  fluid: true,
                  placeholder: t("v8_choose_answer", "Choose answer"),
                  disabled: !canAddFullPin && !canAddPinNote,
                  selectOnBlur: false
                }}
                onChange={value => handleNoteSuggestionDropdownChange(value)}
              />
            </Form.Field>
          )}
        </GridColumn>

        <GridColumn width={2} verticalAlign="top">
          <Form.Field>
            <label className="form-label-placeholder" />
            <div className="checkbox-container">
              <Icon className="circle exclamation" color="red" />
              <span>{t("v8_important", "Important")}</span>
              <FormHelper.Field.Checkbox
                name="visible_important_items"
                formFieldProps={{ className: "checkbox-form-field", disabled: !canAddFullPin }}
                checkboxProps={{ className: "checkbox-box", disabled: !canAddFullPin }}
              />
            </div>
          </Form.Field>
        </GridColumn>
      </Grid>

      <Grid>
        <GridColumn>
          <Form.Field>
            {!noteSuggestionOptions.length && <label>{t("v8_note", "Note")}</label>}
            <FormHelper.Field.TextArea textAreaProps={{ disabled: !canAddFullPin && !canAddPinNote, placeholder: `${t("v8_note", "Note")}`, rows: 2 }} name="note" />
            {isNoteRequired && <Message negative content={t("v8_enter_note_to_save", "Enter a note in order to save")} />}
          </Form.Field>
        </GridColumn>
      </Grid>

      <Grid>
        <GridColumn>
          <NoteAttachments appointmentId={intervention.appointment_id} attachments={attachments} setAttachments={setAttachments} />
        </GridColumn>
      </Grid>

      <div className="warranty-actions">
        <Form.Button color="grey" type="button" className="-appointment-status print-overview-button" onClick={openModal}>
          <Icon className="print black print-overview-icon" />
          <span>{t("v8_print_overview", "Print Overview")}</span>
        </Form.Button>

        <Form.Button color="green" type="submit" disabled={isButtonDisabled}>
          <span>{intervention.pin_history?.length ? t("v8_update", "Update") : t("v8_save", "Save")}</span>
          <Icon className="check" />
        </Form.Button>
      </div>
    </div>
  );
};
